import { FormErrors, FormOwnProps, HiddenConfig, TextConfig } from '@qfa/forms';
import { FormViewType, Crosswalk, CrosswalkInput } from '@kinetic-ui/data-layer';
import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProps } from '../types';
import { SetState, RecursivePartial } from 'types';
import { ViewOptions } from 'components/forms';
import { useForm } from 'kineticUtils/hooks/useForm';

const fieldNames = {
  ID: 'id',
  GEOMETRY: 'geometry',
  DESCRIPTION: 'description',
};

export const newCrosswalkValues: RecursivePartial<CrosswalkInput> = {
  id: '',
  description: '',
  geometry: { type: 'LineString', coordinates: [] },
};

export interface CrosswalkForm {
  values: [
    values: Partial<CrosswalkInput>,
    setValues: SetState<Partial<CrosswalkInput>>,
    reset: (newValues?: Partial<CrosswalkInput>) => void
  ];
  isDirty: boolean;
  formProps: FormOwnProps<CrosswalkInput>;
  errors: [
    hasErrors: boolean,
    errors: FormErrors<CrosswalkInput>,
    setErrors: SetState<FormErrors<CrosswalkInput>>
  ];
  isSaveDisabled: boolean;
  viewProps: ViewOptions;
  setDirtyFields: SetState<string[]>;
}

export const useCrosswalkForm = (
  initialData: Partial<CrosswalkInput> | undefined,
  disabled?: boolean
): CrosswalkForm => {
  const { t } = useTranslation(['locations', 'common']);
  const id = useMemo(() => initialData?.id ?? nanoid(), [initialData?.id]);
  const {
    props: formProps,
    values: [values, setValues, reset],
    errors: [hasErrors, errors, setErrors],
    dirty: [isDirty, _dirtyFields, setDirtyFields],
    isSaveDisabled,
  } = useForm<CrosswalkInput>({
    readOnly: disabled,
    fields: [
      HiddenConfig({
        id: `${fieldNames.ID}${initialData?.id}`,
        name: fieldNames.ID,
        required: true,
        defaultValue: id,
      }),
      HiddenConfig({
        id: `${fieldNames.GEOMETRY}${initialData?.id}`,
        name: fieldNames.GEOMETRY,
        required: true,
      }),
      TextConfig({
        id: `${fieldNames.DESCRIPTION}${initialData?.id}`,
        name: fieldNames.DESCRIPTION,
        required: true,
        label: t('common:description'),
        fieldSize: 'full',
      }),
    ],
    defaultValues: initialData,
  });

  const viewProps: ViewOptions = useMemo(
    () => ({
      type: FormViewType.Embedded,
      title: t('locations:crosswalk'),
      viewId: `crosswalk-${id}`,
      paths: [],
      sectionsOptions: [
        {
          fieldNames: [fieldNames.DESCRIPTION],
          name: t('common:details'),
          hashRoute: `details-${id}`,
          sectionId: `details-${id}`,
        },
      ],
    }),
    [id, t]
  );

  const formValues = values as FormProps;
  const matchedObjectValues: Crosswalk = {
    id: formValues.id,
    description: formValues.description,
    geometry: [],
  };

  return {
    viewProps,
    formProps,
    isDirty,
    setDirtyFields,
    values: [matchedObjectValues, setValues, reset],
    errors: [hasErrors, errors, setErrors],
    isSaveDisabled,
  };
};
